import React, { useState, useEffect } from 'react';
import { Alert, Snackbar, AlertColor } from '@mui/material';

export interface ToastMessage {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface ToastState {
  messages: ToastMessage[];
}

// Простой глобальный менеджер уведомлений
class ToastManager {
  private listeners: Array<(messages: ToastMessage[]) => void> = [];
  private messages: ToastMessage[] = [];

  addMessage(message: string, severity: AlertColor = 'info', duration = 6000) {
    const toast: ToastMessage = {
      id: Date.now().toString() + Math.random().toString(36),
      message,
      severity,
      duration,
    };

    this.messages = [...this.messages, toast];
    this.notifyListeners();

    // Автоматически удаляем через duration
    setTimeout(() => {
      this.removeMessage(toast.id);
    }, duration);
  }

  removeMessage(id: string) {
    this.messages = this.messages.filter(msg => msg.id !== id);
    this.notifyListeners();
  }

  subscribe(listener: (messages: ToastMessage[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.messages));
  }
}

export const toast = new ToastManager();

// Методы-хелперы
export const showSuccess = (message: string) => toast.addMessage(message, 'success');
export const showError = (message: string) => toast.addMessage(message, 'error', 8000);
export const showWarning = (message: string) => toast.addMessage(message, 'warning');
export const showInfo = (message: string) => toast.addMessage(message, 'info');

export const ToastContainer: React.FC = () => {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const unsubscribe = toast.subscribe(setMessages);
    return unsubscribe;
  }, []);

  return (
    <>
      {messages.map((message, index) => (
        <Snackbar
          key={message.id}
          open={true}
          anchorOrigin={{ 
            vertical: 'top', 
            horizontal: 'right' 
          }}
          style={{
            marginTop: `${index * 64}px`, // Стек уведомлений
          }}
          onClose={() => toast.removeMessage(message.id)}
        >
          <Alert 
            onClose={() => toast.removeMessage(message.id)} 
            severity={message.severity}
            sx={{ width: '100%' }}
          >
            {message.message}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};